import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AdminMessagesModal } from "@/components/AdminMessagesModal";
import { useState } from "react";
import { Mail, MessageCircle, Instagram, Youtube, Music2, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import { contactFormSchema } from "@/lib/validation";

export const ContactSection = () => {
  const { isAdmin } = useUserRole();
  const [isMessagesModalOpen, setIsMessagesModalOpen] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = new FormData(e.target as HTMLFormElement);
    const formValues = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      subject: formData.get("subject") as string,
      message: formData.get("message") as string,
    };

    // Validate input using zod schema
    const validation = contactFormSchema.safeParse(formValues);
    
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast({
        title: "Validation Error",
        description: firstError.message,
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("messages")
        .insert([{
          name: validation.data.name,
          email: validation.data.email,
          subject: validation.data.subject,
          message: validation.data.message,
        }]);

      if (error) {
        if (import.meta.env.DEV) console.error("Error submitting message:", error);
        toast({
          title: "Error",
          description: "Error submitting message. Please try again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Message sent successfully!",
        });
        (e.target as HTMLFormElement).reset();
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error("Error submitting message:", error);
      toast({
        title: "Error",
        description: "Error submitting message. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <section id="contact" className="container mx-auto px-4 py-16 border-t border-border/20">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
          Contact Creator
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Get in touch for custom beats, collaborations, or any questions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
        {/* Contact Form */}
        <Card>
          <CardHeader>
            <CardTitle>Send a Message</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" placeholder="Your name" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" placeholder="your@email.com" required />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input id="subject" name="subject" placeholder="What's this about?" required />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea 
                  id="message" 
                  name="message"
                  placeholder="Tell me about your project..." 
                  rows={5}
                  required 
                />
              </div>
              
              <Button type="submit" className="w-full">
                <MessageCircle className="h-4 w-4 mr-2" />
                Send Message
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Email</h3>
                  <p className="text-muted-foreground">info@skpills.com</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                I typically respond within 24 hours. If you have any questions about beat licensing or any other subject regarding beats, hit me up.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">Follow Me</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" size="sm" asChild>
                  <a href="https://www.instagram.com/skpills/" target="_blank" rel="noopener noreferrer">
                    <Instagram className="h-4 w-4 mr-2" />
                    Instagram
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="https://www.youtube.com/@BeatsBySkpills" target="_blank" rel="noopener noreferrer">
                    <Youtube className="h-4 w-4 mr-2" />
                    YouTube
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="https://www.tiktok.com/@skpills" target="_blank" rel="noopener noreferrer">
                    <Music2 className="h-4 w-4 mr-2" />
                    TikTok
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="https://x.com/skpills" target="_blank" rel="noopener noreferrer">
                    <span className="font-bold mr-2">𝕏</span>
                    X
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold mb-2">About Skpills Beats</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Skpills is a premium beats marketplace offering rap, trap, and hip-hop instrumentals for artists worldwide.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Premium rap, trap & hip-hop beats</li>
                <li>• MP3, WAV, and FLP stems available</li>
                <li>• Lease and exclusive licensing options</li>
                <li>• Instant download after purchase</li>
              </ul>
            </CardContent>
          </Card>

          {isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Admin Panel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  View and manage messages from users.
                </p>
                <Button 
                  onClick={() => setIsMessagesModalOpen(true)}
                  className="w-full"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  View Messages
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <AdminMessagesModal
        isOpen={isMessagesModalOpen}
        onClose={() => setIsMessagesModalOpen(false)}
      />
    </section>
  );
};
